import React, { useEffect, useMemo, useRef, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Popover from "@material-ui/core/Popover";
import CircularProgress from "@material-ui/core/CircularProgress";
import { FaWhatsapp, FaTelegramPlane } from "react-icons/fa";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import ReplayIcon from "@material-ui/icons/Replay";
import SyncIcon from "@material-ui/icons/Sync";
import ChatIcon from "@material-ui/icons/Chat";
import PaymentIcon from "@material-ui/icons/Payment";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import MonetizationOnOutlinedIcon from "@material-ui/icons/MonetizationOnOutlined";
import { useDialogStyles } from "../../theme/redesign";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import { accountIsFullSale, accountLive, accountPrizeEligible, accountSellable, addDaysIso, canPayWithWallet, canRenewService, looksLikePhone, markCartPulse, markSubPulse, moneyPair, periodSlotCost, subHasRecentRenew, todayIso, walletNeedOf } from "./helpers";
import { stopRow, tNegocio, useMobile } from "./shared";
import { hasNoValidity, needsSlots, productOfferLabel, sellableProfiles, slotLabel } from "./catalog";
import { AccountOption, ProductMark } from "./AccountPeek";
import { CartItemFields, usableCodeFilters } from "./CartItemFields";
import { useStyles } from "./styles";
import { CodeLookupButton } from "./CodeLookupDialog";
import ValidateBinancePaymentDialog from "./ValidateBinancePaymentDialog";

export const isBinancePaymentMethod = (method) => {
  if (!method) return false;
  if (String(method.provider || "").toLowerCase() === "binance_pay") return true;
  return /binance/i.test(`${method.name || ""} ${method.type || ""}`);
};

export const customerHasBinancePayment = (customer, payments = []) => {
  if (!customer) return false;
  const ids = [customer.cartPaymentMethodId, customer.cartPaymentMethodId2]
    .map((id) => Number(id))
    .filter(Boolean);
  if (!ids.length) {
    return isBinancePaymentMethod(customer.paymentMethod) || isBinancePaymentMethod(customer.sale?.paymentMethod);
  }
  return (payments || []).some((p) => ids.includes(Number(p.id)) && isBinancePaymentMethod(p));
};

const emptyDraft = (sub, renew, customer) => {
  const lastEnd = String(sub.endDate || "").slice(0, 10);
  const startDate = renew ? (lastEnd && lastEnd > todayIso() ? lastEnd : todayIso()) : sub.startDate || todayIso();
  const durationDays = sub.durationDays || 30;
  const pair = moneyPair(sub.price, sub.priceFx);
  return {
    productId: sub.productId || sub.product?.id || "",
    accountId: sub.accountId || "",
    profileId: sub.profileId || "",
    currency: sub.priceCurrency === "fx" ? "fx" : "local",
    price: pair.local,
    priceFx: pair.fx,
    status: renew ? "active" : sub.status || "active",
    startDate,
    durationDays,
    notes: sub.notes || "",
    codesEnabled: customer?.cartCodesEnabled ? (sub.codesEnabled == null ? false : !!sub.codesEnabled) : false,
    codeSubjects: Array.isArray(sub.codeSubjects) ? sub.codeSubjects : sub.codesEnabled ? null : [],
    paid: renew ? true : sub.sale?.status === "paid" || sub.status === "active",
    payWithWallet: false,
    payWithPrize: false,
    endDate: startDate ? addDaysIso(startDate, durationDays) : sub.endDate || "",
  };
};

export function ChannelButtons({ customer, onSend, disabled }) {
  if (!customer) return null;
  return (
    <span>
      {looksLikePhone(customer.number) ? (
        <Tooltip title="WhatsApp">
          <IconButton size="small" disabled={disabled} onClick={stopRow(() => onSend("whatsapp"))} style={{ color: "#25d366" }}>
            <FaWhatsapp size={18} />
          </IconButton>
        </Tooltip>
      ) : null}
      {customer.telegram ? (
        <Tooltip title="Telegram">
          <IconButton size="small" disabled={disabled} onClick={stopRow(() => onSend("telegram"))} style={{ color: "#0088cc" }}>
            <FaTelegramPlane size={18} />
          </IconButton>
        </Tooltip>
      ) : null}
    </span>
  );
}

export function SendChannelButton({ customer, onSend, title, icon, disabled, color }) {
  const [anchor, setAnchor] = useState(null);
  const canSend = !!(looksLikePhone(customer?.number) || customer?.telegram);
  return (
    <>
      <Tooltip title={title}>
        <span>
          <IconButton
            size="small"
            disabled={disabled || !canSend}
            onClick={stopRow((e) => setAnchor(e.currentTarget))}
            style={color && !disabled ? { color } : undefined}
          >
            {icon}
          </IconButton>
        </span>
      </Tooltip>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <div style={{ padding: 4 }}>
          <ChannelButtons
            customer={customer}
            onSend={(channel) => {
              setAnchor(null);
              onSend(channel);
            }}
          />
        </div>
      </Popover>
    </>
  );
}

/** Envía el saldo vigente por el canal elegido, sin modificar el monedero. */
export function PointsSendTrigger({ customer, children, style, disabled = false }) {
  const [anchor, setAnchor] = useState(null);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const canSend = !!customer?.contactId && !!(looksLikePhone(customer?.number) || customer?.telegram);
  const blocked = disabled || sending || !canSend;
  const send = async (channel) => {
    if (disabled || !canSend || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    setAnchor(null);
    try {
      await api.post("/negocio/notify", {
        notify: true,
        kind: "points",
        channel,
        contactId: customer.contactId,
      });
      toast.success(tNegocio("pages.customers.sentOk"));
    } catch (e) {
      toastError(e);
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };
  return (
    <>
      <Tooltip title={canSend ? tNegocio("pages.customers.sendPointsTip") : ""}>
        <span
          role="button"
          tabIndex={blocked ? -1 : 0}
          aria-disabled={blocked}
          aria-busy={sending}
          onClick={stopRow((e) => !blocked && setAnchor(e.currentTarget))}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && !blocked) { e.preventDefault(); setAnchor(e.currentTarget); }
          }}
          style={{ cursor: blocked ? "default" : "pointer", ...style }}
        >
          {React.isValidElement(children) ? React.cloneElement(children, { disabled: blocked || children.props.disabled }) : children}
        </span>
      </Tooltip>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <div style={{ padding: "8px 10px", display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{tNegocio("pages.customers.sendPointsTitle")}</span>
          <ChannelButtons customer={customer} onSend={send} disabled={blocked} />
        </div>
      </Popover>
    </>
  );
}

export function CustomerServiceDialog({
  open,
  mode,
  sub,
  customer,
  onClose,
  onSaved,
}) {
  const classes = useStyles();
  const dlg = useDialogStyles();
  const isMobile = useMobile();
  const [draft, setDraft] = useState(null);
  const [products, setProducts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [saving, setSaving] = useState(false);

  // Las recargas por socket recrean sub/customer: el borrador solo se reinicia al abrir o cambiar de servicio.
  const subRef = useRef(sub);
  subRef.current = sub;
  const customerRef = useRef(customer);
  customerRef.current = customer;
  useEffect(() => {
    if (!open || !subRef.current) return;
    setDraft(emptyDraft(subRef.current, mode === "renew", customerRef.current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sub?.id, mode, customer?.contactId]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [p, a] = await Promise.all([api.get("/negocio/products"), api.get("/negocio/accounts")]);
        setProducts(Array.isArray(p.data?.records) ? p.data.records : []);
        setAccounts(Array.isArray(a.data?.records) ? a.data.records : []);
      } catch (e) {
        toastError(e);
      }
    })();
  }, [open]);

  const patch = (next) => {
    setDraft((d) => {
      const merged = { ...d, ...next };
      if (next.startDate != null || next.days != null || next.durationDays != null) {
        if (next.days != null) merged.durationDays = next.days;
        const start = next.startDate != null ? next.startDate : merged.startDate;
        const days = merged.durationDays;
        merged.endDate = start ? addDaysIso(start, days) : merged.endDate;
      }
      if (next.productId != null) {
        merged.accountId = "";
        merged.profileId = "";
        merged.codeSubjects = null;
      }
      if (next.accountId != null) merged.profileId = "";
      if (next.payWithWallet) {
        merged.paid = true;
        merged.payWithPrize = false;
      }
      if (next.payWithPrize) {
        merged.paid = true;
        merged.payWithWallet = false;
        merged.durationDays = 30;
        merged.days = 30;
      }
      return merged;
    });
  };

  const productAccounts = useMemo(() => {
    const currentId = Number(draft?.accountId || 0);
    return accounts.filter((a) => {
      if (draft?.productId && Number(a.productId) !== Number(draft.productId)) return false;
      if (currentId && Number(a.id) === currentId) return true;
      return draft?.payWithPrize ? accountPrizeEligible(a) : accountSellable(a);
    });
  }, [accounts, draft?.productId, draft?.accountId, draft?.payWithPrize]);
  const visibleProducts = useMemo(() => {
    const currentId = Number(draft?.productId || 0);
    const eligible = draft?.payWithPrize ? accountPrizeEligible : accountSellable;
    const sellableIds = new Set(accounts.filter((a) => eligible(a)).map((a) => Number(a.productId)));
    return products.filter((p) => sellableIds.has(Number(p.id)) || Number(p.id) === currentId);
  }, [products, accounts, draft?.productId, draft?.payWithPrize]);
  const account = productAccounts.find((a) => Number(a.id) === Number(draft?.accountId));
  const profiles = accountLive(account)
    ? sellableProfiles(account, draft?.profileId)
    : sellableProfiles(account, draft?.profileId).filter((p) => Number(p.id) === Number(draft?.profileId));
  const product = visibleProducts.find((p) => Number(p.id) === Number(draft?.productId))
    || products.find((p) => Number(p.id) === Number(draft?.productId));
  const productFilters = usableCodeFilters(product);

  useEffect(() => {
    if (!draft?.codesEnabled || draft.codeSubjects != null) return;
    const prod = products.find((p) => Number(p.id) === Number(draft.productId));
    const available = usableCodeFilters(prod);
    if (!available.length) return;
    patch({ codeSubjects: available.map((filter) => String(filter.subject).trim()) });
  }, [draft?.codesEnabled, draft?.codeSubjects, draft?.productId, products]);

  const save = async () => {
    if (!draft || !sub) return;
    setSaving(true);
    try {
      const payload = {
        productId: draft.productId,
        accountId: draft.accountId || null,
        profileId: accountIsFullSale(account) ? null : draft.profileId || null,
        price: draft.price,
        priceFx: draft.priceFx,
        priceCurrency: draft.currency || "local",
        startDate: draft.startDate,
        durationDays: draft.durationDays,
        notes: draft.notes,
        codesEnabled: !!customer?.cartCodesEnabled && !!draft.codesEnabled,
        codeSubjects: draft.codesEnabled ? draft.codeSubjects || [] : [],
        paid: !!draft.paid || !!draft.payWithWallet || !!draft.payWithPrize,
        payWithWallet: !!draft.payWithWallet,
        payWithPrize: !!draft.payWithPrize,
      };
      if (mode === "renew") {
        await api.patch(`/negocio/subscriptions/${sub.id}`, { ...payload, renew: true, days: draft.durationDays });
        markSubPulse(sub.id, "renewed");
        markCartPulse(customer?.contactId || sub.contactId, "renewed");
      } else {
        await api.patch(`/negocio/subscriptions/${sub.id}`, { ...payload, update: true });
        markCartPulse(customer?.contactId || sub.contactId, "update");
      }
      toast.success(tNegocio("toastSaved"));
      onSaved(mode === "renew" ? "renew" : "update", sub);
    } catch (e) {
      toastError(e);
    } finally {
      setSaving(false);
    }
  };

  if (!open || !draft) return null;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      classes={{ paper: dlg.paper }}
    >
      <DialogTitle>
        {mode === "renew" ? tNegocio("renew") : tNegocio("edit")} · {customer?.name || ""}
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          select
          fullWidth
          margin="dense"
          variant="outlined"
          label={tNegocio("fields.product")}
          value={draft.productId || ""}
          onChange={(e) => patch({ productId: e.target.value })}
        >
          {visibleProducts.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              <span className={classes.optionRow}>
                <ProductMark product={p} size={24} circle />
                {productOfferLabel(p, tNegocio)}
              </span>
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          margin="dense"
          variant="outlined"
          label={tNegocio("cols.account")}
          value={draft.accountId || ""}
          onChange={(e) => {
            const id = e.target.value;
            const next = productAccounts.find((a) => Number(a.id) === Number(id));
            patch({
              accountId: id,
              profileId: accountIsFullSale(next) ? "" : draft.profileId,
            });
          }}
        >
          <MenuItem value="">{tNegocio("fields.none")}</MenuItem>
          {productAccounts.map((a) => (
            <MenuItem key={a.id} value={a.id}>
              <AccountOption account={a} email={a.email || a.code || a.id} />
            </MenuItem>
          ))}
        </TextField>
        {!accountIsFullSale(account) && needsSlots(product || account) ? (
        <TextField
          select
          fullWidth
          margin="dense"
          variant="outlined"
          label={slotLabel(product || account, tNegocio)}
          value={draft.profileId || ""}
          onChange={(e) => patch({ profileId: e.target.value })}
        >
          <MenuItem value="">{tNegocio("fields.assignLater")}</MenuItem>
          {profiles.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.name} {p.pin ? `· ${p.pin}` : ""}
            </MenuItem>
          ))}
        </TextField>
        ) : null}
        <CartItemFields
          startDate={draft.startDate}
          days={draft.durationDays}
          currency={draft.currency}
          price={draft.price}
          priceFx={draft.priceFx}
          cost={periodSlotCost(account || product, hasNoValidity(product) ? 30 : draft.durationDays)}
          notes={draft.notes}
          codesEnabled={draft.codesEnabled}
          codeSubjects={draft.codeSubjects || []}
          codeFilters={productFilters}
          paid={draft.paid || draft.payWithWallet || draft.payWithPrize}
          payWithWallet={draft.payWithWallet}
          payWithPrize={draft.payWithPrize}
          allowWallet={mode === "renew" || sub?.sale?.status === "pending_payment"}
          customer={customer}
          cartHasItems
          showCodes={!!customer?.cartCodesEnabled}
          hideDuration={hasNoValidity(product)}
          onChange={patch}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tNegocio("close")}</Button>
        <Button color="primary" variant="contained" disabled={saving} onClick={save}>
          {tNegocio("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function SendPromptDialog({ prompt, onClose, onSend, onTemplates, onAddAnother }) {
  const dlg = useDialogStyles();
  const [sending, setSending] = useState(null);
  useEffect(() => {
    setSending(null);
  }, [prompt]);
  if (!prompt) return null;
  const send = async (channel) => {
    if (sending) return;
    setSending(channel);
    try {
      await onSend(prompt, channel);
    } finally {
      setSending(null);
    }
  };
  const isNew = prompt.kind === "last" || prompt.kind === "sale";
  const isRenew = prompt.kind === "renew" || prompt.kind === "renewed";
  const canAddAnother = typeof onAddAnother === "function" && (prompt.cart || prompt.kind === "last");
  const title = isRenew
    ? tNegocio("pages.customers.sendRenewTitle")
    : isNew
      ? tNegocio("pages.customers.sendNewServiceTitle")
      : tNegocio("pages.customers.sendPromptTitle");
  const body = isRenew
    ? tNegocio("pages.customers.sendRenewBody")
    : isNew
      ? tNegocio("pages.customers.sendNewServiceBody")
      : tNegocio("pages.customers.sendPromptBody");
  return (
    <Dialog open={!!prompt} onClose={sending ? undefined : onClose} maxWidth="xs" fullWidth classes={{ paper: dlg.paper }}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {body}
        {sending ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14, fontWeight: 600, color: "#1e3a8a" }}>
            <CircularProgress size={18} />
            {tNegocio("pages.customers.sendingVia").replace("{channel}", sending === "telegram" ? "Telegram" : "WhatsApp")}
          </div>
        ) : null}
      </DialogContent>
      <DialogActions style={{ flexWrap: "wrap", gap: 4 }}>
        <Button onClick={onClose} disabled={!!sending}>{tNegocio("pages.customers.sendSkip")}</Button>
        {canAddAnother ? (
          <Button color="primary" onClick={() => onAddAnother(prompt)} disabled={!!sending}>
            {tNegocio("pages.customers.addAnotherProduct")}
          </Button>
        ) : null}
        {onTemplates ? (
          <Button size="small" onClick={onTemplates} disabled={!!sending}>
            {tNegocio("pages.customers.templatesLink")}
          </Button>
        ) : null}
        <ChannelButtons customer={prompt.customer} onSend={send} disabled={!!sending} />
      </DialogActions>
    </Dialog>
  );
}

export const markServicePaid = async (sub) => {
  const saleId = sub?.sale?.id || sub?.saleId || null;
  if (saleId) {
    await api.patch(`/negocio/sales/${saleId}`, { status: "paid" });
    if (sub.status === "pending") await api.patch(`/negocio/subscriptions/${sub.id}`, { status: "active" });
  } else {
    await api.patch(`/negocio/subscriptions/${sub.id}`, { status: "active" });
  }
};

export function ServiceRowActions({ sub, customer, payments, onEdit, onRenew, onNotify, onDeleted, onChanged }) {
  const [confirm, setConfirm] = useState(false);
  const [swapConfirm, setSwapConfirm] = useState(false);
  const [paidConfirm, setPaidConfirm] = useState(false);
  const [walletConfirm, setWalletConfirm] = useState(false);
  const [binanceOpen, setBinanceOpen] = useState(false);
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);
  const due = canRenewService(sub);
  const notifyPending = sub.notifyPending === true || sub.notifyPending === 1;
  const recentRenew = subHasRecentRenew(sub, customer?.contactId);
  const unpaid = sub.sale?.status === "pending_payment" || sub.status === "pending";
  const saleId = sub.sale?.id || sub.saleId || null;
  const showBinanceValidate =
    !!saleId &&
    unpaid &&
    (customerHasBinancePayment(customer, payments) || isBinancePaymentMethod(sub.sale?.paymentMethod));

  const notify = (kind, channel) => onNotify({ kind, channel, subscriptionId: sub.id, contactId: customer.contactId });

  const remove = async () => {
    try {
      await api.delete(`/negocio/subscriptions/${sub.id}`);
      toast.success(tNegocio("toastSaved"));
      setConfirm(false);
      onDeleted();
    } catch (e) {
      toastError(e);
    }
  };

  const swapProfile = async () => {
    try {
      await api.post(`/negocio/subscriptions/${sub.id}/swap-profile`);
      toast.success(tNegocio("pages.customers.swapProfileOk"));
      setSwapConfirm(false);
      (onChanged || onDeleted)?.();
    } catch (e) {
      toastError(e);
    }
  };

  const canSwap = !!(sub?.accountId && sub?.profileId && sub?.status !== "cancelled");

  const walletNeed = Math.max(
    walletNeedOf({ customer, currency: sub.priceCurrency, price: sub.price, priceFx: sub.priceFx }),
    walletNeedOf({ customer, price: Math.abs(Number(sub.sale?.total || 0)) })
  );
  const canPayWallet = !!saleId && unpaid && canPayWithWallet({ customer, need: walletNeed });
  const payWithWallet = async () => {
    try {
      await api.post(`/negocio/sales/${saleId}/pay-wallet`);
      toast.success(tNegocio("pages.customers.paidWithWallet"));
      setWalletConfirm(false);
      (onChanged || onDeleted)?.();
    } catch (e) {
      toastError(e);
    }
  };

  const markPaid = async () => {
    try {
      await markServicePaid(sub);
      toast.success(tNegocio("pages.customers.markedPaid"));
      setPaidConfirm(false);
      (onChanged || onDeleted)?.();
    } catch (e) {
      toastError(e);
    }
  };

  return (
    <div className="actionCluster" onClick={(e) => e.stopPropagation()} style={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}>
      {showBinanceValidate ? (
        <Tooltip title={tNegocio("pages.binance.validateTitle")}>
          <IconButton size="small" onClick={() => setBinanceOpen(true)} style={{ color: "#f0b90b" }}>
            <AccountBalanceWalletIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      {canPayWallet ? (
        <Tooltip title={tNegocio("pages.customers.payWithWallet")}>
          <IconButton size="small" onClick={() => setWalletConfirm(true)} style={{ color: "#6a1b9a" }}>
            <MonetizationOnOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      {unpaid ? (
        <Tooltip title={tNegocio("markPaid")}>
          <IconButton size="small" onClick={() => setPaidConfirm(true)} style={{ color: "#2e7d32" }}>
            <CheckCircleOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      <CodeLookupButton sub={sub} customer={customer} />
      <Tooltip title={tNegocio("pages.products.remove")}>
        <IconButton size="small" onClick={() => setConfirm(true)} style={{ color: "#c62828" }}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={tNegocio("edit")}>
        <IconButton size="small" onClick={() => onEdit(sub)} style={{ color: "#1565c0" }}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {canSwap ? (
        <Tooltip title={tNegocio("pages.customers.swapProfile")}>
          <IconButton size="small" onClick={() => setSwapConfirm(true)} style={{ color: "#6a1b9a" }}>
            <SwapHorizIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      <SendChannelButton
        title={tNegocio("pages.services.cart.send")}
        icon={<ChatIcon fontSize="small" />}
        color="#00897b"
        customer={customer}
        onSend={(channel) => notify("sale", channel)}
      />
      {unpaid ? (
        <SendChannelButton
          title={tNegocio("pages.services.cart.sendPay")}
          icon={<PaymentIcon fontSize="small" />}
          color="#ed6c02"
          customer={customer}
          onSend={(channel) => notify("pay", channel)}
        />
      ) : null}
      {due ? (
        <Tooltip title={tNegocio("renew")}>
          <IconButton size="small" onClick={() => onRenew(sub)} style={{ color: "#6a1b9a" }}>
            <ReplayIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      {recentRenew ? (
        <SendChannelButton
          title={tNegocio("pages.customers.sendRenewed")}
          icon={<ReplayIcon fontSize="small" />}
          color="#2e7d32"
          customer={customer}
          onSend={(channel) => notify("renew", channel)}
        />
      ) : null}
      {notifyPending && !recentRenew ? (
        <SendChannelButton
          title={tNegocio("pages.customers.sendUpdated")}
          icon={<SyncIcon fontSize="small" />}
          color="#0277bd"
          customer={customer}
          onSend={(channel) => notify("update", channel)}
        />
      ) : null}
      <ConfirmationModal
        title={tNegocio("pages.customers.removeServiceTitle")}
        open={confirm}
        onClose={() => setConfirm(false)}
        onConfirm={remove}
      >
        {tNegocio("pages.customers.removeServiceBody")}
      </ConfirmationModal>
      <ConfirmationModal
        title={tNegocio("markPaid")}
        open={paidConfirm}
        onClose={() => setPaidConfirm(false)}
        onConfirm={markPaid}
      >
        {tNegocio("pages.customers.markPaidBody")}
      </ConfirmationModal>
      <ConfirmationModal
        title={tNegocio("pages.customers.payWithWallet")}
        open={walletConfirm}
        onClose={() => setWalletConfirm(false)}
        onConfirm={payWithWallet}
      >
        {tNegocio("pages.customers.payWithWalletBody")}
      </ConfirmationModal>
      <ConfirmationModal
        title={tNegocio("pages.customers.swapProfileTitle")}
        open={swapConfirm}
        onClose={() => setSwapConfirm(false)}
        onConfirm={swapProfile}
      >
        {tNegocio("pages.customers.swapProfileBody")}
      </ConfirmationModal>
      <ValidateBinancePaymentDialog
        open={binanceOpen}
        saleId={saleId}
        onClose={() => setBinanceOpen(false)}
        onPaid={() => {
          setBinanceOpen(false);
          (onChanged || onDeleted)?.();
        }}
      />
    </div>
  );
}
